import type { RuleSummary } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../context/ArbiterClientContext';
import { usePaginatedApi } from './usePaginatedApi';

interface UseRuleVersionsResult {
  /** All versions, newest first. The current version row keeps the `@CURRENT` alias in `version`. */
  versions: RuleSummary[];
  /** Canonical number of the current version, once the list has loaded; undefined if it failed. */
  latestVersion: string | undefined;
  loading: boolean;
  error: string | null;
}

/** Fetches every page of ListRuleVersions for a rule, following continuation tokens. */
export function useRuleVersions(ruleName: string | undefined): UseRuleVersionsResult {
  const client = useArbiterClient();

  const { data, loading, error } = usePaginatedApi(
    (fetchParams) =>
      client.listRuleVersions(fetchParams).then((r) => ({
        items: r.rules ?? [],
        continuationToken: r.continuationToken,
      })),
    { ruleName: ruleName ?? '' },
    { enabled: !!ruleName },
  );

  const versions = [...data].sort((a, b) => Number(b.canonicalVersion ?? 0) - Number(a.canonicalVersion ?? 0));

  // The current row carries the @CURRENT alias. If a response ever lacks it, the highest version is the
  // next best answer; only an outright failure leaves this undefined.
  const latestVersion = versions.find((v) => v.version === '@CURRENT')?.canonicalVersion
    ?? versions[0]?.canonicalVersion;
  return { versions, latestVersion, loading, error };
}
